import { useEffect, useState, useMemo } from 'react';
import { DateRange } from 'common/constants';
import { DatePicker } from '@mui/x-date-pickers';
import { Box, Typography } from '@mui/material';

type DateRangePickerDialogProps = {
  selected: DateRange,
  setRange: Function,
  className: string,
  inputName: string,
  required: boolean,
  disabled: boolean,
  gap?: number,
  errors?: any
}

export default function DateRangePickerDialog({ selected, setRange, className, inputName, required, disabled, gap = 4, errors }: DateRangePickerDialogProps) {
  const [dateFrom, setDateFrom] = useState<Date | null>(selected?.from ? new Date(selected.from) : null);
  const [dateTo, setDateTo] = useState<Date | null>(selected?.to ? new Date(selected.to) : null);

  useEffect(() => {
    if (selected) {
      setDateFrom(selected?.from ? new Date(selected.from) : null);
      setDateTo(selected?.to ? new Date(selected.to) : null);
    } else {
      setDateFrom(null);
      setDateTo(null);
    }
  }, [selected]);

  const isFromInvalid = useMemo(() => {
    if (!dateFrom || !dateTo) return false;
    return dateFrom > dateTo;
  }, [dateFrom, dateTo]);

  const isToInvalid = useMemo(() => {
    if (!dateFrom || !dateTo) return false;
    return dateTo < dateFrom;
  }, [dateFrom, dateTo]);

  const handleChange = (newDate: Date | null, key: string) => {
    if (key === 'from') {
      setRange({ from: newDate, to: dateTo });
    } else if (key === 'to') {
      setRange({ from: dateFrom, to: newDate });
    }
  }

  const renderDatePicker = (date: Date | null, key : string) => {
    const isError = (key === 'from' && isFromInvalid) || (key === 'to' && isToInvalid);

    return (
      <DatePicker
        value={date}
        onChange={(d: Date | null) => {
          handleChange(d, key);
        }}      
        format="P"
        disabled={disabled}
        name={`${inputName}-${key}`}
        slotProps={{
          textField: {
            size: 'small',
            className: 'w-100',
            InputProps: { required },
            error: isError,
          },
        }}
      />
    )
  };

  return (
    <Box className={`${className} w-100 d-flex`} gap={gap}>
        {renderDatePicker(dateFrom,'from')}
        {renderDatePicker(dateTo,'to')}
        {errors?.range && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
        {errors?.range}
      </Typography>}
    </Box>
  );
}
